import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Typography,
  Button,
  Spinner
} from "@material-tailwind/react";
import { Helmet } from "react-helmet";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import Layout from "../components/layout/Layout";
import { colors } from "../utils/colors";
import axiosInstance from "../utils/axiosInstance";
import { trackEvent } from "../utils/analytics";

// PDF d'exemple Little Worker
const pdfUrl = "/little-worker.pdf"; // Assurez-vous que ce PDF est dans le dossier public

export default function LittleWorkerOfferPage() {
  const [isMobile, setIsMobile] = useState(false);
  const { propertyId } = useParams(); // Récupère propertyId de l'URL s'il existe
  const navigate = useNavigate();

  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Pour le chargement initial des infos utilisateur

  // Gère la détection du mode mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Simule la récupération des infos utilisateur (email) au chargement
  useEffect(() => {
    const fetchUserInfo = async () => {
      setIsLoading(true);
      try {
        // Exemple: décoder un token ou appeler une API /me
        const token = document.cookie.split('; ').find(row => row.startsWith('authToken='));
        if (token) {
          const tokenValue = token.split('=')[1];
          JSON.parse(atob(tokenValue.split('.')[1]));
        }
      } catch (err) {
        console.error("Erreur récupération info utilisateur:", err);
        // Gérer l'erreur si nécessaire
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserInfo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    trackEvent('little_worker_offer_page_submit', {
      propertyId: propertyId || "N/A",
      ademeNumberProvided: false // Changé car on ne demande plus le numéro ADEME
    });

    try {
      await axiosInstance.post('/deals/log-click', {
        dealId: 'little-worker',
        provider: 'Little Worker',
        category: 'energie',
        source: 'little_worker_offer_page',
        propertyId: propertyId || null, // Envoyer propertyId s'il existe
      });
      
      trackEvent('little_worker_offer_page_submit_success', {
        propertyId: propertyId || "N/A"
      });
      
      setSuccess("Votre demande de simulation a été prise en compte et vous sera adressée sous 24h.");
      // Optionnel: rediriger ou afficher un message différent
    } catch (err) {
      trackEvent('little_worker_offer_page_submit_error', {
        propertyId: propertyId || "N/A",
        error: err.response?.data?.message || err.message || "Unknown error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPdfViewer = () => (
    <div className="w-full h-full" style={{ aspectRatio: "1 / 1.4142" }}>
      {isMobile ? (
        <iframe
          src={`https://docs.google.com/viewer?url=${encodeURIComponent(window.location.origin + pdfUrl)}&embedded=true`}
          className="w-full border-0 h-full"
          title="Exemple Rapport Little Worker"
        />
      ) : (
        <object
          data={pdfUrl}
          type="application/pdf"
          className="w-full h-full border-0"
        >
          <iframe
            src={`https://docs.google.com/viewer?url=${encodeURIComponent(window.location.origin + pdfUrl)}&embedded=true`}
            className="w-full h-full border-0"
            title="Exemple Rapport Little Worker"
          />
        </object>
      )}
    </div>
  );
  
  if (isLoading) {
    return (
        <Layout>
            <div className="flex justify-center items-center h-screen">
                <Spinner className="h-12 w-12" style={{ color: colors.dimoPrimary }} />
            </div>
        </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Offre Little Worker - Simulation Énergétique Gratuite</title>
        <meta
          name="description"
          content="Découvrez comment Little Worker peut vous aider à améliorer la performance énergétique de votre logement."
        />
        <link
          rel="canonical"
          href={`https://client.dimo-diagnostic.net/offre/little-worker${propertyId ? `/${propertyId}` : ""}`}
        />
      </Helmet>

      <div className={`flex ${isMobile ? "flex-col" : "flex-row"} gap-6`}>
        {/* -- PARTIE GAUCHE (Formulaire et Infos) -- */}
        <div className={`${isMobile ? "w-full" : "w-1/2"} mb-6`}>
          <Card className="shadow-md p-6 space-y-6">
            <Typography variant="h3" color="blue-gray" className="mb-2 text-center md:text-left">
              Votre Simulation Énergétique Offerte avec Little Worker
            </Typography>
            
            {success ? (
              <div className="flex flex-col items-center justify-center space-y-6 py-8">
                <CheckCircleIcon className="h-20 w-20 text-green-500" />
                <Typography variant="h5" color="green" className="text-center">
                  Demande Envoyée !
                </Typography>
                <Typography color="blue-gray" className="text-center">
                  {success}
                </Typography>
                <Button
                  variant="filled"
                  style={{ backgroundColor: colors.dimoPrimary }}
                  onClick={() => propertyId ? navigate(`/diagnostics/${propertyId}`) : navigate('/dashboard')}
                >
                  {propertyId ? "Retour au diagnostic" : "Retour à l'accueil"}
                </Button>
              </div>
            ) : (
              <>
                <div className="text-gray-700 space-y-3 text-sm md:text-base">
                    <Typography variant="h6" className="font-semibold text-dimo-primary-dark">
                        Augmentez la valeur de votre bien jusqu'à 7% et réduisez de 18% votre consommation énergétique grâce à une rénovation optimisée !
                    </Typography>
                    <Typography>
                        Vous souhaitez améliorer la performance énergétique de votre logement ? DIMO Diagnostic et Little Worker s'associent pour vous offrir une <strong>simulation énergétique complète et gratuite</strong>.
                    </Typography>
                    <Typography>
                        En quelques clics, obtenez une vision claire des optimisations possibles pour votre bien :
                    </Typography>
                    <ul className="list-disc list-inside space-y-1 pl-4">
                        <li>Identification des forces et faiblesses de votre logement (schéma de déperditions).</li>
                        <li>Scénarios de rénovation personnalisés pour booster votre note DPE.</li>
                        <li>Estimations des coûts des travaux et des économies réalisables pour chaque scénario.</li>
                        <li>Calcul des bénéfices annuels suite à une rénovation.</li>
                    </ul>
                    <Typography>
                        Pour recevoir votre simulation personnalisée sous 24h, il vous suffit de cliquer sur le bouton ci-dessous.
                    </Typography>
                    <Typography variant="small" className="italic">
                        Vous pouvez consulter un <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="text-dimo-primary hover:text-dimo-primary-dark font-medium underline">exemple de rapport de simulation ici</a>.
                    </Typography>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6 pt-4">
                  <Button
                    type="submit"
                    variant="filled"
                    style={{ backgroundColor: colors.dimoPrimary, color: colors.dimoPop }}
                    className="w-full text-base py-3"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <Spinner className="h-5 w-5 mx-auto" />
                    ) : (
                      "Obtenir ma simulation offerte"
                    )}
                  </Button>
                </form>
                 <Typography variant="small" className="text-gray-500 text-xs pt-2">
                    En cliquant sur "Obtenir ma simulation offerte", vous reconnaissez que vos données personnelles seront traitées conformément à la{' '}

                    <a 
                        href="https://www.dimo-diagnostic.net/protection-des-donnees" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:text-blue-700 underline"
                    >
                        politique de confidentialité de DIMO DIAGNOSTIC
                    </a>.
                 </Typography>
              </>
            )}
          </Card>
        </div>

        {/* -- PARTIE DROITE (PDF) -- */}
        <div
          className={`${
            isMobile ? "w-full" : "w-1/2"
          } flex justify-start items-start mb-6`}
        >
          <Card
            className="shadow-md overflow-hidden sticky top-6" // sticky pour le desktop
            style={{
              height: isMobile ? "60vh" : "calc(100vh - 120px)", // Ajuster la hauteur pour le sticky
              width: "100%"
            }}
          >
            {renderPdfViewer()}
          </Card>
        </div>
      </div>
    </Layout>
  );
} 